import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/Button';
import Loading from '@/components/Loading';
import { MdKeyboardArrowLeft } from 'react-icons/md';
import useFetchJobSourcesServices from '../api/useFetchJobSourcesServices';
import useCreateJobSource, { isPollingSourceType } from '../hooks/useCreateJobSource';
import JobSourceCredentialForm from '../components/forms/JobSourceCredentialForm';
import JobSourceUrlForm from '../components/forms/JobSourceUrlForm';
import LinkedAccountLoading from '../components/LinkedAccountLoading';
import { listPathFor, resolveServiceDisplayName } from '../utils/serviceGroup';
import type { JobSourceServiceType } from '@/types/serializers';

type Props = {
  sourceType: string;
};

export default function LinkedAccountSourceTemplate({ sourceType }: Props) {
  const navigate = useNavigate();
  const { data: services, isPending } = useFetchJobSourcesServices();
  const {
    createWithCredentials,
    createWithUrls,
    isPending: isSubmitting,
    isPolling,
  } = useCreateJobSource();
  const [pollingServiceType, setPollingServiceType] = useState<JobSourceServiceType>();

  const backToSelection = () => navigate({ to: '/agent/linked_accounts/new' });
  const goToList = (serviceType?: JobSourceServiceType) =>
    navigate({ to: listPathFor(serviceType) });

  if (isPending) {
    return (
      <div className="flex h-[calc(100vh_-_200px)] items-center justify-center">
        <Loading size="medium" />
      </div>
    );
  }

  const service = (services ?? []).find((s) => s.source_type === sourceType);

  if (!service) {
    return (
      <div className="flex h-[calc(100vh_-_200px)] flex-col items-center justify-center gap-md">
        <p className="text-body-md">指定された連携先が見つかりませんでした。</p>
        <Button type="button" color="secondary" onClick={backToSelection}>
          <MdKeyboardArrowLeft size={20} />
          アカウント選択へ戻る
        </Button>
      </div>
    );
  }

  if (isPolling && isPollingSourceType(service.source_type)) {
    return <LinkedAccountLoading listPath={listPathFor(pollingServiceType)} />;
  }

  const backButton = (
    <Button
      type="button"
      color="secondary"
      className="h-[52px] w-[208px] gap-2 px-4"
      onClick={backToSelection}
    >
      <MdKeyboardArrowLeft size={20} />
      アカウント選択へ戻る
    </Button>
  );

  const isUrlType = service.auth_type === 'URL' || service.auth_type === 'AGENT_URL';

  return (
    <div className="flex w-full items-center justify-center py-lg">
      {isUrlType ? (
        <JobSourceUrlForm
          service={service}
          isSubmitting={isSubmitting}
          footer={backButton}
          onSubmit={async (values) => {
            const result = await createWithUrls({
              sourceType: service.source_type,
              urls: values.urls,
              loginId: values.loginId,
              loginPass: values.loginPass,
            });
            if (result.status === 'success') goToList(result.serviceType);
          }}
        />
      ) : (
        <JobSourceCredentialForm
          service={service}
          serviceName={resolveServiceDisplayName(sourceType, service)}
          isSubmitting={isSubmitting}
          footer={backButton}
          onSubmit={async (values) => {
            const result = await createWithCredentials({
              sourceType: service.source_type,
              loginId: values.loginId,
              loginPass: values.loginPass,
              accessUrl: values.accessUrl || undefined,
              basicAuthId: values.basicAuthId || undefined,
              basicAuthPass: values.basicAuthPass || undefined,
            });
            if (result.status === 'polling') setPollingServiceType(result.serviceType);
            if (result.status === 'success') goToList(result.serviceType);
          }}
        />
      )}
    </div>
  );
}
